import {GetRenderer} from "./renderer.js";
import {worker} from "./worker-service.js";
import {Email, AuthToken, EnsureFreshToken, Reauthenticate} from "./auth.js";
import {measureServers, rankServers, setConnectedServer} from "./relay-choice.js";

import {Builder} from "flatbuffers/js/flexbuffers/builder.js";

import {Transport} from "./transport.js";

import {Pointers} from "./pointers.js";
import {AudioPlayer} from "./audio.js";
import * as telemetry from "./telemetry.js";
import {$, detailOf} from "./dom.js";

export const EventStreamDisconnected = "streamdisconnected";
export const EventStreamConnected = "streamconnected";
export const EventStreamConnecting = "streamconnecting";
export const EventIDRWaiting = "evtIDRWait";
export const EventIDRReceived = "evtIDRRecv";
export const EventStreamReconnecting = "streamreconnecting";
export const EventStreamStale = "streamstale";

// Minimum gap between keyframe requests on the wire, matching the native
// client's lastIDRRequestTime throttle. Asking faster is pointless: StreamBGFX
// only emits an IDR if 5s have passed since the last one
// (StreamBGFX.cpp: `idrRequested && now - lastIdrSent > 5'000'000`).
const IDR_REQUEST_INTERVAL_MS = 2000;

// No frames for this long means the stream is stale, matching the native
// client's 2s disconnect timer.
const STALE_STREAM_MS = 2000;
const STALE_CHECK_INTERVAL_MS = 500;

// Requested sizes below this are ignored, matching the native client's guard
// against a minimised window degrading the shared stream.
const MIN_REQUESTED_SIZE = 256;

// Skip sending input once this much is already waiting on the socket.
const MAX_INPUT_BUFFERED_BYTES = 4096;
// Finger-distance change (CSS px) per wheel tick while pinching. apg's own GLFW touch
// handler uses 26px; each tick zooms its image view 1.1x.
const PINCH_PX_PER_TICK = 26;

function isEditableTarget(target) {
	if (!target || !target.tagName) return false;
	const tag = target.tagName.toLowerCase();
	return (
		tag === "input" || tag === "textarea" || tag === "select" || target.isContentEditable === true
	);
}

let _videoScale = Number(window.localStorage.getItem("request-video-scale") || "1.0");
document.addEventListener("RequestVideoScaleChanged", (event) => {
	_videoScale = detailOf(event).scale;
});

class Impl {
	constructor() {
		this.websocket = null;

		this.source = null;
		this.stopped = false;
		this.staleWatchdog = null;
		this.connectAbort = new AbortController();
		this.inputRaf = null;
		this.knownUsers = new Set();
		this.lastUsersKey = null;
		this.waitingIDR = true;
		this.forceIDR = false;
		this.requestIDRGate = null;
		this.lastIDRRequestTime = 0;
		this.lastVersion = null;
		this.lastPacketNumber = 0;
		this.lastGoodRequestedWidth = 1920;
		this.lastGoodRequestedHeight = 1080;
		this.textDecoder = new TextDecoder("utf-8");
		this.audio = null;

		// #SetupCanvas
		const canvas = document.createElement("canvas");
		const offscreen = canvas.transferControlToOffscreen();
		const rendererName = GetRenderer();
		// Probing takes ~375ms and needs only a codec and a size, so it starts now
		// rather than when the first packet lands. H264 because it is what every
		// browser can decode; if the app turns out to be sending HEVC the pipeline
		// rebuilds, which is exactly what it used to do anyway.
		worker.postMessage(
			{
				type: "start",
				rendererName,
				canvas: offscreen,
				prewarm: {
					codec: 0,
					width: this.lastGoodRequestedWidth,
					height: this.lastGoodRequestedHeight,
				},
			},
			[offscreen],
		);

		this.canvas = () => canvas;

		this.canvasroot = null;
		this.containerRect = null;
		this.canvasRect = null;
		this.canvassizeObserver = null;

		// Mouse state
		this.mouseX = 0;
		this.mouseY = 0;
		this.mouseWheelDeltaX = 0;
		this.mouseWheelDeltaY = 0;
		this.mouseButtons = 0;
		this.pendingClickButtons = 0;
		this.mouseOnWindow = false;

		this.touchClickSimulation = 0; // 1 - started, 2 - started-ended, 3 - ended
		this.touchClickSimulationTicker = []; // 1 - started, 2 - started-ended, 3 - ended, 4 - half step of started
		this.pinchDist = null; // finger distance while pinching, null otherwise
		this.pinchAcc = 0;

		this.pressedKeyCodes = {};
		this.tapKeys = []; // pressed for one input message, then released
		this.inputchars = [];
		// Last text synced either way, so neither side echoes the other's update.
		this.clipboardText = "";
		this.clipboardToSend = null;

		this.instanceID = Math.round(Math.random() * (1 << 30));
		this.username = null;

		this.pointers = null;
	}

	setup(source, canvasroot) {
		if (typeof source == "undefined" || typeof canvasroot == "undefined") {
			throw "source or canvasroot are not defined";
		}

		this.source = source;
		this.username = Email() || "anon";

		if (typeof canvasroot == "string") {
			this.canvasroot = $(canvasroot);
		} else if ("appendChild" in canvasroot) {
			this.canvasroot = canvasroot;
		}

		if (!this.canvasroot) {
			throw "canvas root not found";
		}

		this.canvasroot.appendChild(this.canvas());

		this.pointers = new Pointers();

		const freezedCanvasRoot = this.canvasroot;

		const canvassizeObserver = new ResizeObserver(() => {
			this.updateContainerRect(freezedCanvasRoot);
		});

		// #Canvas Input Events. Mouse and Keyboard
		const onkeydown = (event) => {
			this.onKeyDownHandler(event);
		};
		const onkeyup = (event) => {
			this.onKeyUpHandler(event);
		};
		const onblur = () => {
			this.onBlurHandler();
		};
		// Clipboard sync, as the native Remote does it: the app's clipboard is kept
		// equal to the browser's, and Ctrl+V reaches the app untouched so it pastes
		// from its own. A page cannot poll the clipboard like native does, so it is
		// sent when the browser pastes. The paste event fires during the Ctrl+V
		// keydown, so text and keys go out in the same input message, and the app
		// applies the clipboard before the keys.
		const onpaste = (event) => {
			if (isEditableTarget(event.target) || window.viewonly) return;
			const text = event.clipboardData?.getData("text/plain");
			if (!text || text === this.clipboardText) return;
			this.clipboardText = text;
			this.clipboardToSend = text;
		};
		// The cached canvas origin is viewport-relative, so anything that scrolls
		// invalidates it. Capture phase to catch scrolling inside any container, and
		// passive so it never delays the scroll itself.
		const onscroll = () => {
			this.canvasRect = null;
		};

		// Latch the press: a click that starts and ends between two sends would
		// otherwise never appear in any snapshot. The native client does the same.
		const onmousedown = (event) => {
			this.saveCursorPos(event);
			this.mouseButtons = event.buttons;
			this.pendingClickButtons |= event.buttons;
		};
		const onmouseup = (event) => {
			this.saveCursorPos(event);
			this.mouseButtons = event.buttons;
		};
		const onmousemove = (event) => {
			this.saveCursorPos(event);
		};
		const onmouseenter = () => {
			this.mouseOnWindow = true;
		};
		const onmouseleave = () => {
			this.mouseOnWindow = false;
		};
		const oncontextmenu = (event) => {
			this.mouseButtons = event.buttons;
			event.preventDefault();
		};
		// deltaMode 1 is lines and 2 is pages (Firefox reports lines), so raw deltas
		// are ~40x smaller there and scrolling felt broken.
		const onwheel = (event) => {
			const unit = event.deltaMode === 1 ? 40 : event.deltaMode === 2 ? 400 : 1;
			this.mouseWheelDeltaX -= event.deltaX * unit * 0.01;
			this.mouseWheelDeltaY -= event.deltaY * unit * 0.01;
			event.preventDefault();
		};
		const workerCallback = (event) => {
			this.handleWorkerMessage(event);
		};

		// Two fingers pinch, sent as wheel ticks the way apg's native touch handler does
		// (the protocol has no gesture field): release the button so the view does not
		// drag, pin the cursor at the midpoint so zoom anchors there, and tick the wheel
		// per PINCH_PX_PER_TICK of spread. Apart = positive = zoom in.
		const fingerDist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);
		const ontouchstart = (event) => {
			if (event.touches.length >= 2) {
				const [a, b] = event.touches;
				this.saveCursorPos({
					clientX: (a.clientX + b.clientX) / 2,
					clientY: (a.clientY + b.clientY) / 2,
				});
				this.pinchDist = fingerDist(event.touches);
				this.pinchAcc = 0;
				this.touchClickSimulation = 0;
				this.touchClickSimulationTicker = [];
				this.mouseButtons = 0;
				return;
			}
			if (this.pinchDist !== null) return;
			this.saveCursorPos(event.changedTouches[0]);
			if (this.touchClickSimulation === 0) this.touchClickSimulation = 1;
		};
		const ontouchend = (event) => {
			// Lifting fingers after a pinch must not click; the gesture ends when all are up.
			if (this.pinchDist !== null) {
				if (event.touches.length === 0) this.pinchDist = null;
				return;
			}
			this.saveCursorPos(event.changedTouches[0]);
			if (this.touchClickSimulation === 0) this.touchClickSimulation = 3;
			else this.touchClickSimulation = 2;
		};
		const ontouchcancel = (event) => {
			if (this.pinchDist !== null) {
				if (event.touches.length === 0) this.pinchDist = null;
				return;
			}
			if (this.touchClickSimulation === 0) this.touchClickSimulation = 3;
			else this.touchClickSimulation = 2;
		};
		const ontouchmove = (event) => {
			if (this.pinchDist === null) {
				this.saveCursorPos(event.changedTouches[0]);
				return;
			}
			if (event.touches.length < 2) return;
			const dist = fingerDist(event.touches);
			this.pinchAcc += dist - this.pinchDist;
			this.pinchDist = dist;
			while (Math.abs(this.pinchAcc) >= PINCH_PX_PER_TICK) {
				const tick = Math.sign(this.pinchAcc);
				this.mouseWheelDeltaY += tick;
				this.pinchAcc -= tick * PINCH_PX_PER_TICK;
			}
		};

		const canvas = this.canvas();
		this.updateContainerRect(this.canvasroot);
		this.detachEventListener = () => {
			window.removeEventListener("keydown", onkeydown);
			window.removeEventListener("keyup", onkeyup);
			window.removeEventListener("blur", onblur);
			window.removeEventListener("paste", onpaste);
			window.removeEventListener("scroll", onscroll, true);

			canvas.removeEventListener("mousemove", onmousemove);
			canvas.removeEventListener("mouseenter", onmouseenter);
			canvas.removeEventListener("mouseleave", onmouseleave);
			canvas.removeEventListener("mouseup", onmouseup);
			canvas.removeEventListener("mousedown", onmousedown);

			canvas.removeEventListener("contextmenu", oncontextmenu);
			canvas.removeEventListener("wheel", onwheel);

			canvas.removeEventListener("touchstart", ontouchstart);
			canvas.removeEventListener("touchend", ontouchend);
			canvas.removeEventListener("touchcancel", ontouchcancel);
			canvas.removeEventListener("touchmove", ontouchmove);

			worker.removeEventListener("message", workerCallback);

			canvassizeObserver.unobserve(freezedCanvasRoot);
		};

		this.attachEventListener = () => {
			window.addEventListener("keydown", onkeydown);
			window.addEventListener("keyup", onkeyup);
			window.addEventListener("blur", onblur);
			window.addEventListener("paste", onpaste);
			window.addEventListener("scroll", onscroll, {capture: true, passive: true});

			canvas.addEventListener("mousemove", onmousemove);
			canvas.addEventListener("mouseenter", onmouseenter);
			canvas.addEventListener("mouseleave", onmouseleave);
			canvas.addEventListener("mouseup", onmouseup);
			canvas.addEventListener("mousedown", onmousedown);

			canvas.addEventListener("touchstart", ontouchstart, false);
			canvas.addEventListener("touchend", ontouchend, false);
			canvas.addEventListener("touchcancel", ontouchcancel, false);
			canvas.addEventListener("touchmove", ontouchmove, false);

			canvas.addEventListener("contextmenu", oncontextmenu);
			canvas.addEventListener("wheel", onwheel);

			worker.addEventListener("message", workerCallback);

			canvassizeObserver.observe(freezedCanvasRoot);
		};
	}

	Cleanup() {
		const canvas = this.canvas();

		if (canvas) {
			canvas.parentNode.removeChild(canvas);
			this.canvas = () => null;
		}
	}

	/**
	 * One video packet, already parsed. Presence and canvas sizing happen here;
	 * decoding happens in the worker.
	 * @param {import("./metadata.js").VideoPacket} packet
	 */
	handlePacket(packet) {
		if (this.stopped) return;
		const canvas = this.canvas();
		if (!canvas) return;

		// The serialisation version is echoed back in every input message.
		this.lastVersion = packet.version;

		// frameTime is the sender's clock in microseconds, so this is only
		// meaningful when the clocks are synced — the native client assumes the same.
		const glassToGlassMs = packet.frameTime > 0 ? Date.now() - packet.frameTime / 1000 : null;
		// The whole message, so the number stays comparable to the wire bandwidth.
		telemetry.countFrame(packet.messageBytes, glassToGlassMs);

		// Another user (or the app) has forced this client to view-only. Handled here,
		// not inside updatePresence: the command is addressed to us by uniqueId and has
		// nothing to do with the presence list — and the app sends it on ONE frame and
		// then clears it, so a miss is permanent.
		if (packet.userToMakeViewOnly !== null && packet.userToMakeViewOnly === this.instanceID) {
			document.dispatchEvent(new CustomEvent("b6-make-current-user-view-only"));
		}

		// The app sends its clipboard once, on change, to every viewer. View-only
		// viewers ignore it, as in native. Writing can fail (tab not focused, or
		// Safari, which wants a user gesture); the next paste then sends the browser's
		// clipboard back, so nothing gets stuck.
		if (packet.clipboardText !== null) {
			this.clipboardText = packet.clipboardText;
			if (!window.viewonly) navigator.clipboard?.writeText(packet.clipboardText).catch(() => {});
		}

		this.updatePresence(packet, canvas);
		this.fitCanvas(packet, canvas);

		// Before the video is transferred to the worker: the audio frames are views
		// into the same buffer. Independent of key frames, so it plays while waiting.
		if (packet.audio.length > 0) {
			if (!this.audio) this.audio = new AudioPlayer();
			this.audio.push(packet.audioParams, packet.audio);
		}

		// Everything below needs a decodable frame.
		if (this.waitingIDR && !packet.isIDR) return;

		if (this.waitingIDR) {
			this.waitingIDR = false;
			telemetry.setState("live");
			document.dispatchEvent(new Event(EventIDRReceived));
		}
		// The latch is cleared only by an actual key frame, so a request lost on the
		// way to the app is retried rather than silently dropped.
		if (packet.isIDR) this.forceIDR = false;

		if (packet.packetNumber !== this.lastPacketNumber + 1 && !packet.isIDR) {
			// A discontinuity means frames were lost, so the reference chain is broken
			// until the next key frame.
			console.log(`frame gap: ${this.lastPacketNumber} -> ${packet.packetNumber}`);
			this.forceIDR = true;
			telemetry.countGap();
		}
		this.lastPacketNumber = packet.packetNumber;

		worker.postMessage(
			{
				type: "data",
				codec: packet.codec,
				data: packet.data,
				isIDR: packet.isIDR,
				frameTime: packet.frameTime,
				width: packet.width,
				height: packet.height,
			},
			[packet.data.buffer],
		);
	}

	/** Remote cursors and the viewers list. */
	updatePresence(packet, canvas) {
		// A packet that does not report presence at all (older apps omit userInfo)
		// must leave the viewers list alone. Treating "not reported" as "nobody here"
		// would wipe the list on a single such packet.
		if (packet.users === null) return;

		// getBoundingClientRect() forces layout, so read it once per packet rather
		// than once per user.
		const canvasRect = canvas.getBoundingClientRect();
		const ids = [];

		for (const user of packet.users) {
			ids.push(user.id);

			if (!this.knownUsers.has(user.id)) {
				this.knownUsers.add(user.id);
				// The shape must match the b6-viewers-list listener in router.js.
				document.dispatchEvent(new CustomEvent("b6-users-added", {detail: {user}}));
			}

			// Keyed by uniqueId: usernames are not unique (two tabs, or two people
			// sharing an email prefix), so matching on the name could hide a real
			// remote cursor or mistarget view-only.
			if (user.id !== this.instanceID && user.cursor) {
				this.pointers.CreatePointer(user.id, user.name);
				this.pointers.SetPointerLocation(
					user.id,
					user.cursor.x,
					user.cursor.y,
					canvasRect,
					canvas.scale,
				);
			}
		}

		// knownUsers exists only to avoid re-dispatching an add for someone already
		// shown, so it has to track the CURRENT set. Left as an append-only set it
		// would permanently suppress anyone who dropped out of one packet and came
		// back — and the removal path below is now live, so that happens.
		for (const id of this.knownUsers) {
			if (!ids.includes(id)) this.knownUsers.delete(id);
		}

		// Presence rarely changes, and dispatching per packet meant 60 DOM events a
		// second for nothing.
		const key = ids.join(",");
		if (key !== this.lastUsersKey) {
			this.lastUsersKey = key;
			document.dispatchEvent(new CustomEvent("b6-users-updated", {detail: {users: ids}}));
		}
		this.pointers.KeepVisible(ids);
	}

	/** Letterbox the canvas to the stream's aspect ratio. */
	fitCanvas(packet, canvas) {
		if (
			packet.width === canvas.receivedVideoWidth &&
			packet.height === canvas.receivedVideoHeight
		) {
			return;
		}
		const scale = Math.min(
			1,
			Math.min(this.containerRect.height / packet.height, this.containerRect.width / packet.width),
		);
		canvas.receivedVideoWidth = packet.width;
		canvas.receivedVideoHeight = packet.height;
		canvas.style.width = Math.round(packet.width * scale) + "px";
		canvas.style.height = Math.round(packet.height * scale) + "px";
		canvas.scale = scale;
		// The canvas just moved, so the cached origin is stale.
		this.canvasRect = null;
	}

	/**
	 * Top-left of the canvas in client coordinates.
	 *
	 * Cached because the alternative is a forced synchronous layout on every mouse
	 * and touch event — over a hundred a second while dragging, for a value that only
	 * changes when the canvas is letterboxed or the container resizes, and both of
	 * those already invalidate it. This is the same mistake the per-frame presence
	 * loop used to make.
	 */
	cursorOrigin() {
		if (!this.canvasRect) {
			this.canvasRect = this.canvas().getBoundingClientRect();
		}
		return this.canvasRect;
	}

	saveCursorPos(event) {
		const canvas = this.canvas();
		// A queued touch/mouse event can still arrive after the session stopped.
		if (!canvas) return;
		const rect = this.cursorOrigin();
		this.mouseX = (event.clientX - rect.left) / (canvas.scale || 1);
		this.mouseY = (event.clientY - rect.top) / (canvas.scale || 1);
	}

	StartListen() {
		this.attachEventListener();

		const userinfo = this.username;
		const instanceID = this.instanceID;

		const connectToStream = async (source) => {
			telemetry.reset();
			telemetry.setState("connecting");
			// The token is handed to /api/connect and then to the WebSocket, which is
			// authenticated once at upgrade and then lives for hours. Renewing here means a
			// session never starts on a token that is about to run out.
			await EnsureFreshToken();
			document.dispatchEvent(new CustomEvent(EventStreamConnecting, {detail: {source}}));
			const connectResp = await fetch("/api/connect", {
				method: "POST",
				body: JSON.stringify({source}),
				signal: this.connectAbort.signal,
				headers: {
					Authorization: "Bearer " + AuthToken(),
				},
			});
			if (connectResp.status === 401) {
				// Expired or rejected token. Reauthenticate() navigates away, so the
				// message below is only ever seen when it declines to redirect again.
				Reauthenticate();
				throw new Error("Your session has expired. Sign in again.");
			}
			if (!connectResp.ok) {
				throw new Error(`/api/connect failed: ${connectResp.status}`);
			}
			const connectJson = await connectResp.json();
			if ("error" in connectJson) {
				throw new Error(connectJson.error);
			}
			if (!connectJson.servers || connectJson.servers.length === 0) {
				throw new Error("no stream servers available");
			}
			if (this.stopped) {
				return;
			}

			// Measured in parallel and cached for the page, so this costs one round of
			// small requests per session rather than per stream. rankServers keeps the
			// server's own order — origin last — whenever nothing could be measured.
			const ranked = rankServers(connectJson.servers, await measureServers(connectJson.servers));
			if (this.stopped) {
				return;
			}
			const serverUrl = ranked[0];
			setConnectedServer(serverUrl);
			// The token is read per attempt, not captured once: it expires, and a
			// redial presenting a stale one can never succeed. Input is never
			// queued — a full-state snapshot is worthless once superseded.
			// The token is re-read per attempt rather than captured once: it expires,
			// and a redial presenting a stale one can never succeed.
			const transport = new Transport({
				url: serverUrl,
				protocols: () => [AuthToken()],
				onPacket: (packet) => this.handlePacket(packet),
				onServerError: (text) => {
					if (this.stopped) return;
					console.error("the bridge rejected this connection:", text);
					telemetry.setState("error");
					document.dispatchEvent(
						new CustomEvent(EventStreamDisconnected, {
							// A fixed message: the raw frame is server JSON, which is
							// meaningless to a user and untrusted in the UI.
							detail: {source, message: "The server refused the connection."},
						}),
					);
				},
				onClosed: () => {
					if (this.stopped) return;
					// The bridge went away (app restart, ZMQ key change, redeploy). It
					// redials by itself, but the UI has to stop claiming the stream is
					// live and the next one must start from a key frame.
					this.waitingIDR = true;
					telemetry.countReconnect();
					telemetry.setState("reconnecting");
					document.dispatchEvent(new CustomEvent(EventStreamReconnecting, {detail: {source}}));
				},
				onError: () => {
					if (this.stopped) return;
					telemetry.setState("reconnecting");
				},
			});

			this.websocket = transport;
			telemetry.setState("waiting");
			document.dispatchEvent(new CustomEvent(EventStreamConnected, {detail: {workload: source}}));

			// Stale-stream watchdog. Without it a stream that simply stops — app
			// hung, network black-holed, bridge wedged — leaves the UI showing
			// "Live" over a frozen frame indefinitely. The native client does the
			// same thing with its 2s disconnect timer.
			const connectedAt = Date.now();
			let staleDecodeMark = 0;
			this.staleWatchdog = window.setInterval(() => {
				if (this.stopped) return;
				// Measured from the connect too, so a stream that never delivers a
				// first frame is reported instead of sitting on "Waiting for source".
				const lastFrame = telemetry.lastFrameAt() || connectedAt;
				const since = Date.now() - lastFrame;
				if (telemetry.state() !== "stale") {
					staleDecodeMark = telemetry.framesDecoded();
				}
				if (since > STALE_STREAM_MS) {
					if (telemetry.state() !== "stale") {
						console.warn("no frames for", since, "ms");
						// forceIDR, NOT waitingIDR: this asks for a key frame without
						// gating deltas. Gating them would turn a self-healing pause
						// into a freeze lasting until the app's next key frame, which
						// can be 5s away.
						this.forceIDR = true;
						telemetry.setState("stale");
						document.dispatchEvent(
							new CustomEvent(EventStreamStale, {detail: {source, msSinceLastFrame: since}}),
						);
					}
				} else if (
					telemetry.state() === "stale" &&
					telemetry.hasDecodedFrame() &&
					telemetry.framesDecoded() > staleDecodeMark
				) {
					// Bytes arriving is not the same as pictures appearing: only treat
					// the stream as live again once the decoder has actually produced
					// a frame since the stall. The real-IDR path dispatches
					// EventIDRReceived; never synthesise one here.
					telemetry.setState("live");
				}
			}, STALE_CHECK_INTERVAL_MS);

			// Paced by the display, like the native client (which sends once per
			// render tick) instead of a fixed 30ms, so input latency tracks the
			// refresh rate. requestAnimationFrame stops in a hidden tab, so a slow
			// interval keeps the control path warm there.
			const sendInput = () => {
				if (this.stopped) return;
				if (this.lastVersion !== null) {
					this.sendRemoteInput(transport, source, userinfo, instanceID, this.lastVersion);
				}
			};
			const tick = () => {
				if (this.stopped) return;
				sendInput();
				this.inputRaf = window.requestAnimationFrame(tick);
			};
			this.inputRaf = window.requestAnimationFrame(tick);
			this.userInputSendInterval = window.setInterval(() => {
				if (document.hidden) sendInput();
			}, 100);
		};

		connectToStream(this.source).catch((err) => {
			if (this.stopped || err.name === "AbortError") return;
			console.error("could not connect to stream", err);
			telemetry.setState("error");
			document.dispatchEvent(
				new CustomEvent(EventStreamDisconnected, {
					detail: {source: this.source, message: String((err && err.message) || err)},
				}),
			);
		});
	}

	remove() {
		const canvas = this.canvas();

		if (canvas && canvas.parentNode) {
			canvas.parentNode.removeChild(canvas);
			this.canvas = () => null;
		}
	}

	StopListen() {
		// Set before anything else: an in-flight connectToStream checks this after
		// each await, so a stop during connect cannot leave a live socket and input
		// timer running with no handle to them.
		this.stopped = true;
		this.detachEventListener();

		// Clear Pointers
		this.pointers.ClearAll();

		if (this.websocket != null) {
			this.websocket.close();
			this.websocket = null;
		}

		if (this.userInputSendInterval) {
			window.clearInterval(this.userInputSendInterval);
			this.userInputSendInterval = null;
		}

		if (this.inputRaf) {
			window.cancelAnimationFrame(this.inputRaf);
			this.inputRaf = null;
		}

		if (this.staleWatchdog) {
			window.clearInterval(this.staleWatchdog);
			this.staleWatchdog = null;
		}

		if (this.requestIDRGate) {
			window.clearTimeout(this.requestIDRGate);
			this.requestIDRGate = null;
		}

		this.connectAbort.abort();

		if (this.audio) {
			this.audio.close();
			this.audio = null;
		}

		// Let the worker drop its decoder so the next session starts clean.
		worker.postMessage({type: "stop"});
		document.dispatchEvent(new Event("b6-session-closed"));

		this.remove();
	}

	handleWorkerMessage(event) {
		if (event.data === "kfr") {
			this.waitingIDR = true;
			return;
		}

		if (event.data && event.data.type === "stats") {
			telemetry.setDecoderStats(event.data);
			return;
		}

		if (event.data && event.data.type === "decoder-unavailable") {
			// WebCodecs is missing here, so no stream can ever play. Say so plainly
			// rather than leaving a black canvas labelled "Live".
			telemetry.setState("error");
			document.dispatchEvent(
				new CustomEvent(EventStreamDisconnected, {
					detail: {source: this.source, message: event.data.reason},
				}),
			);
			return;
		}

		if (event.data && event.data.type === "codec-unsupported") {
			telemetry.setCodecUnsupported(event.data.codec);
			telemetry.setState("error");
			// This browser has no decoder for the stream's codec (e.g. HEVC on
			// Firefox, or Windows without the platform extension). Say so instead
			// of leaving the UI on "Live" over a black canvas.
			const codecName = event.data.codec === 1 ? "HEVC" : "H264";
			document.dispatchEvent(
				new CustomEvent(EventStreamDisconnected, {
					detail: {
						source: this.source,
						message: `This browser cannot decode ${codecName} video.`,
					},
				}),
			);
		}
	}

	GetButtonArray() {
		if (this.touchClickSimulation > 0) {
			// We got start-end in the same frame
			if (this.touchClickSimulation === 2) {
				this.touchClickSimulationTicker.push(1);
				this.touchClickSimulationTicker.push(3);
			} else {
				this.touchClickSimulationTicker.push(this.touchClickSimulation);
			}
			this.touchClickSimulation = 0;
		}

		if (this.touchClickSimulationTicker.length > 0) {
			let ticker = this.touchClickSimulationTicker[0];
			if (ticker === 1) {
				this.mouseButtons = 0;
				this.touchClickSimulationTicker[0] = 5;
			} else if (ticker > 4) {
				this.mouseButtons = 0;
				this.touchClickSimulationTicker[0] = this.touchClickSimulationTicker[0] - 1;
			} else if (ticker > 3) {
				this.mouseButtons = 1;
				this.touchClickSimulationTicker[0] = this.touchClickSimulationTicker[0] - 1;
				if (ticker === 4) {
					this.touchClickSimulationTicker.shift();
				}
			} else if (ticker === 3) {
				this.mouseButtons = 0;
				this.touchClickSimulationTicker.shift();
			}
		}

		// OR in any press that happened since the last send, then clear it, so a
		// sub-tick click is reported exactly once.
		const buttons = this.mouseButtons | this.pendingClickButtons;
		this.pendingClickButtons = 0;

		return [
			Boolean(buttons & 0x01),
			Boolean((buttons >> 1) & 0x01),
			Boolean((buttons >> 2) & 0x01),
			Boolean((buttons >> 3) & 0x01),
			Boolean((buttons >> 4) & 0x01),
		];
	}

	sendRemoteInput(websocket, source, userinfo, instanceID, version) {
		if (websocket == null) {
			return;
		}

		// Do not pile input onto a socket that is not draining: the app only ever
		// needs the newest snapshot, and a backlog here is pure added latency. This
		// is the browser-side equivalent of the native client's capped sndbuf.
		if (websocket.bufferedAmount > MAX_INPUT_BUFFERED_BYTES) {
			return;
		}

		const scale = window.devicePixelRatio * (isFinite(_videoScale) ? _videoScale : 1.0);
		const rect = this.containerRect;

		const builder = new Builder();
		builder.startVector();

		builder.addInt(version);

		builder.addFloat(this.mouseX);
		builder.addFloat(this.mouseY);

		builder.startVector();
		const mouseButtons = this.GetButtonArray();
		for (let i = 0; i < mouseButtons.length; i++) {
			builder.add(mouseButtons[i]);
		}
		// The `false` forces an UNTYPED vector, which the C++ app's deserialiser
		// depends on. It comes from patches/flatbuffers@24.3.7.patch, so the shipped
		// .d.ts declares end() with no argument — hence the cast, not a deletion.
		/** @type {any} */ (builder).end(false);

		builder.addFloat(this.mouseWheelDeltaY); // mouse wheel
		builder.addFloat(this.mouseWheelDeltaX); // mouse wheel h

		builder.add(this.mouseOnWindow); // mouse on window

		// Older apps (serialisation version < 3) use a different range for the
		// modifier keys, so send both. The previous loop used `for...in` over
		// Object.keys(), which iterates array INDICES ("0","1",...), so the
		// 648-651 test never matched and the remap never ran.
		const pressed = [];
		for (const key in this.pressedKeyCodes) {
			if (!this.pressedKeyCodes[key]) continue;
			const code = Number(key);
			if (code === 0) continue;
			pressed.push(code);
			if (version < 3 && code >= 648 && code <= 651) {
				pressed.push(code - 648 + 641);
			}
		}
		builder.add(new Uint16Array(pressed));

		const analogValuesIndexes = new Uint16Array(0);
		builder.add(analogValuesIndexes);
		const analogValues = new Float32Array(0);
		builder.add(analogValues);

		const inputchars = new Uint16Array(this.inputchars.length);
		for (let j = 0; j < this.inputchars.length; j++) {
			inputchars[j] = this.inputchars[j].charCodeAt(0);
		}
		builder.add(inputchars);

		// Rate-limit the wire flag to one request per IDR_REQUEST_INTERVAL_MS,
		// matching the native client's lastIDRRequestTime. Input is sent every
		// 30ms, so without this a single dropped frame asks for a keyframe ~33
		// times a second — and keyframes are 5-10x a delta, so an IDR storm makes
		// the congestion that caused the drop strictly worse.
		const needIDR = Boolean(this.waitingIDR || this.forceIDR);
		const now = Date.now();
		const requestIDR = needIDR && now - this.lastIDRRequestTime >= IDR_REQUEST_INTERVAL_MS;

		builder.add(requestIDR);
		// Only update the requested size when it is sensible: the app re-encodes
		// for EVERY client at the requested size, so a minimised or collapsed
		// window would otherwise shrink the stream for everyone. Native applies
		// the same >256 guard with sticky last-good values.
		const reqW = Math.floor(rect.width * scale);
		const reqH = Math.floor(rect.height * scale);
		if (reqW > MIN_REQUESTED_SIZE && reqH > MIN_REQUESTED_SIZE) {
			this.lastGoodRequestedWidth = reqW;
			this.lastGoodRequestedHeight = reqH;
		}
		builder.addInt(this.lastGoodRequestedWidth); //requestedWidth
		builder.addInt(this.lastGoodRequestedHeight); // requestedHeight

		builder.add(window.viewonly); // viewOnly1080

		builder.add(userinfo); // name

		builder.addUInt(instanceID); //uniqueId

		// clipboardText, a trailing std::optional: an untyped vector of 0 or 1 items.
		// Left off entirely when there is nothing new; the app reads trailing fields
		// only if present.
		if (this.clipboardToSend !== null) {
			builder.startVector();
			builder.add(this.clipboardToSend);
			/** @type {any} */ (builder).end(false);
		}

		builder.end();

		const uint8 = builder.finish();

		const msg = new Uint8Array(4 + uint8.byteLength);
		const enc = new TextEncoder(); // always UTF-8; "flex" is ASCII either way
		msg.set(enc.encode("flex"), 0);
		msg.set(new Uint8Array(uint8), 4);

		if (requestIDR) {
			if (this.requestIDRGate == null) {
				this.requestIDRGate = setTimeout(() => {
					this.requestIDRGate = null;
					if (this.stopped) return;
					if (this.waitingIDR || this.forceIDR) {
						document.dispatchEvent(new CustomEvent(EventIDRWaiting));
					}
				}, 1000);
			}
			console.log("Requesting IDR");
		}

		this.inputchars = [];
		this.clipboardToSend = null;
		for (const key of this.tapKeys) this.pressedKeyCodes[key] = false;
		this.tapKeys = [];

		this.mouseWheelDeltaY = 0;
		this.mouseWheelDeltaX = 0;

		// Returns false when the socket is down; nothing to do, the next tick sends
		// current state anyway.
		const sent = websocket.send(msg);

		// Stamped only once the request has actually gone. A dropped message used to
		// burn the whole interval anyway, so a key frame request lost to a socket that
		// was briefly down cost another 2s before the client would even ask again —
		// on exactly the flaky links where recovery matters most.
		if (requestIDR && sent) {
			this.lastIDRRequestTime = now;
			telemetry.countIDRRequest();
		}
	}

	ToImGuiKey(ke) {
		let keyCode = ke.code;

		switch (keyCode) {
			case "Tab":
				return 512;
			case "ArrowLeft":
				return 513;
			case "ArrowRight":
				return 514;
			case "ArrowUp":
				return 515;
			case "ArrowDown":
				return 516;
			case "PageUp":
				return 517;
			case "PageDown":
				return 518;
			case "Home":
				return 519;
			case "End":
				return 520;
			case "Insert":
				return 521;
			case "Delete":
				return 522;
			case "Backspace":
				return 523;
			case "Space":
				return 524;
			case "Enter":
				return 525;
			case "Escape":
				return 526;
			case "Quote":
				return 584;
			case "Comma":
				return 585;
			case "Minus":
				return 586;
			case "Period":
				return 587;
			case "Slash":
				return 588;
			case "Semicolon":
				return 589;
			case "Equal":
				return 590;
			case "BracketLeft":
				return 591;
			case "Backslash":
				return 592;
			case "BracketRight":
				return 593;
			case "Backquote":
				return 594;
			case "CapsLock":
				return 595;
			case "ScrollLock":
				return 596;
			case "NumLock":
				return 597;
			case "PrintScreen":
				return 598;
			case "Pause":
				return 599;
			case "Numpad0":
				return 600;
			case "Numpad1":
				return 601;
			case "Numpad2":
				return 602;
			case "Numpad3":
				return 603;
			case "Numpad4":
				return 604;
			case "Numpad5":
				return 605;
			case "Numpad6":
				return 606;
			case "Numpad7":
				return 607;
			case "Numpad8":
				return 608;
			case "Numpad9":
				return 609;
			case "NumpadDecimal":
				return 610;
			case "NumpadDivide":
				return 611;
			case "NumpadMultiply":
				return 612;
			case "NumpadSubtract":
				return 613;
			case "NumpadAdd":
				return 614;
			case "NumpadEnter":
				return 615;
			case "NumpadEqual":
				return 616;
			case "ControlLeft":
				return 527;
			case "ShiftLeft":
				return 528;
			case "AltLeft":
				return 529;
			case "MetaLeft":
				return 530;
			case "ControlRight":
				return 531;
			case "ShiftRight":
				return 532;
			case "AltRight":
				return 533;
			case "MetaRight":
				return 534;
			case "ContextMenu":
				return 535;
			case "Digit0":
				return 536;
			case "Digit1":
				return 537;
			case "Digit2":
				return 538;
			case "Digit3":
				return 539;
			case "Digit4":
				return 540;
			case "Digit5":
				return 541;
			case "Digit6":
				return 542;
			case "Digit7":
				return 543;
			case "Digit8":
				return 544;
			case "Digit9":
				return 545;
			case "KeyA":
				return 546;
			case "KeyB":
				return 547;
			case "KeyC":
				return 548;
			case "KeyD":
				return 549;
			case "KeyE":
				return 550;
			case "KeyF":
				return 551;
			case "KeyG":
				return 552;
			case "KeyH":
				return 553;
			case "KeyI":
				return 554;
			case "KeyJ":
				return 555;
			case "KeyK":
				return 556;
			case "KeyL":
				return 557;
			case "KeyM":
				return 558;
			case "KeyN":
				return 559;
			case "KeyO":
				return 560;
			case "KeyP":
				return 561;
			case "KeyQ":
				return 562;
			case "KeyR":
				return 563;
			case "KeyS":
				return 564;
			case "KeyT":
				return 565;
			case "KeyU":
				return 566;
			case "KeyV":
				return 567;
			case "KeyW":
				return 568;
			case "KeyX":
				return 569;
			case "KeyY":
				return 570;
			case "KeyZ":
				return 571;
			case "F1":
				return 572;
			case "F2":
				return 573;
			case "F3":
				return 574;
			case "F4":
				return 575;
			case "F5":
				return 576;
			// The named-key range is contiguous: F1=572 ... F12=583. The old table
			// skipped 577, so F6-F11 sent the wrong F-key and F12 sent Quote (584);
			// F13-F24 aliased onto punctuation and letters (F18 sent KeyO).
			case "F6":
				return 577;
			case "F7":
				return 578;
			case "F8":
				return 579;
			case "F9":
				return 580;
			case "F10":
				return 581;
			case "F11":
				return 582;
			case "F12":
				return 583;
			// F13-F24 have no slot in this ImGui version; sending anything would
			// press an unrelated key.
			case "F13":
			case "F14":
			case "F15":
			case "F16":
			case "F17":
			case "F18":
			case "F19":
			case "F20":
			case "F21":
			case "F22":
			case "F23":
			case "F24":
				return 0;
			default:
				return 0;
		}
	}

	GetModKey(ke) {
		let keyCode = ke.code;

		switch (keyCode) {
			case "ControlLeft":
				return 648;
			case "ShiftLeft":
				return 649;
			case "AltLeft":
				return 650;
			case "MetaLeft":
				return 651;
			case "ControlRight":
				return 648;
			case "ShiftRight":
				return 649;
			case "AltRight":
				return 650;
			case "MetaRight":
				return 651;
			default:
				return null;
		}
	}

	onKeyDownHandler(ke) {
		// Typing in the app's own UI (filter box, dialogs) must not be forwarded to
		// the remote app: these listeners are on window, not the canvas.
		if (isEditableTarget(ke.target)) return;

		// Mac's Cmd+C/V/X become Ctrl+C/V/X, the only shortcuts the app knows. Sent as
		// a tap, released once it has gone out: macOS fires no keyup for a letter
		// while Cmd is held, and a held Ctrl+V repeats the paste. Cmd itself is hidden,
		// as ImGui wants exactly Ctrl.
		// ponytail: no platform check; Cmd is Meta only on Mac and the Windows/Linux
		// Super+C/V/X chords are taken by the OS before the browser sees them.
		if (ke.metaKey && !ke.ctrlKey && ["KeyC", "KeyV", "KeyX"].includes(ke.code)) {
			const key = this.ToImGuiKey(ke);
			this.pressedKeyCodes[651] = false;
			this.pressedKeyCodes[648] = true;
			this.pressedKeyCodes[key] = true;
			this.tapKeys.push(648, key);
			return;
		}

		this.pressedKeyCodes[this.ToImGuiKey(ke)] = true;
		let modKey = this.GetModKey(ke);
		if (modKey !== null) {
			this.pressedKeyCodes[modKey] = true;
		}

		// A modified keypress is a shortcut, not typed text; sending it as a
		// character types a stray letter in the remote app.
		const modified = ke.ctrlKey || ke.metaKey || (ke.altKey && !ke.getModifierState("AltGraph"));
		if (!modified && (ke.key.length === 1 || ke.code === "Space")) this.inputchars.push(ke.key);
	}

	onKeyUpHandler(ke) {
		if (isEditableTarget(ke.target)) return;
		// A tap is released once sent; a quick one would otherwise never go out.
		if (this.tapKeys.includes(this.ToImGuiKey(ke))) return;

		this.pressedKeyCodes[this.ToImGuiKey(ke)] = false;
		let modKey = this.GetModKey(ke);
		if (modKey !== null) {
			this.pressedKeyCodes[modKey] = false;
		}
	}

	onBlurHandler() {
		// Release all pressed keys on focus lost
		for (const key in this.pressedKeyCodes) {
			this.pressedKeyCodes[key] = false;
		}
	}

	updateContainerRect(canvasrootNode) {
		this.containerRect = canvasrootNode.getBoundingClientRect();
		this.canvasRect = null;

		const canvas = this.canvas();
		canvas.receivedVideoWidth = 0;
		canvas.receivedVideoHeight = 0;
	}
}

export class StreamSession {
	#impl = new Impl();

	constructor(source, canvasroot) {
		this.#impl.setup(source, canvasroot);
	}

	StartListen() {
		this.#impl.StartListen();
	}

	StopListen() {
		this.#impl.StopListen();
	}
}
